import {
  Shield,
  ScanEye,
  GitBranch,
  FileText,
  DollarSign,
  CheckCircle,
  Lock,
  Zap,
  Terminal,
} from 'lucide-react';
import { useInView } from '../hooks/useInView';
import type { ReactNode } from 'react';

interface Feature {
  icon: ReactNode;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: <ScanEye className="h-6 w-6" aria-hidden="true" />,
    title: 'AI Safety Detectors',
    description:
      'Detect PII, secrets, bias, toxicity, prompt injection, IP leakage, and RAG injection in real time. Seven built-in detector categories protect every AI interaction.',
  },
  {
    icon: <Shield className="h-6 w-6" aria-hidden="true" />,
    title: 'Policy Engine',
    description:
      'Define and enforce granular AI usage policies per team, model, and use case. Compile policy bundles with hash-chain verification for tamper-proof governance.',
  },
  {
    icon: <GitBranch className="h-6 w-6" aria-hidden="true" />,
    title: 'Multi-Provider Routing',
    description:
      'Route requests across OpenAI, Anthropic, Azure OpenAI, Google Vertex AI, and AWS Bedrock based on cost, latency, and compliance. Built-in fallback chains ensure high availability.',
  },
  {
    icon: <Lock className="h-6 w-6" aria-hidden="true" />,
    title: 'Security & Access Control',
    description:
      'Enterprise-grade security with MFA enforcement, RBAC, personal access tokens, API key rotation, session management, and SCIM provisioning.',
  },
  {
    icon: <Zap className="h-6 w-6" aria-hidden="true" />,
    title: 'Workflow Automation',
    description:
      'Event-driven workflows with automated responses and compliance actions. Define triggers for policy violations, cost thresholds, and anomalous usage patterns.',
  },
  {
    icon: <Terminal className="h-6 w-6" aria-hidden="true" />,
    title: 'Developer Experience',
    description:
      'Full CLI for config export/import, audit export, and bundle deployment. OpenAI-compatible SDK and comprehensive REST API for seamless integration.',
  },
  {
    icon: <CheckCircle className="h-6 w-6" aria-hidden="true" />,
    title: 'Compliance & Governance',
    description:
      'Built-in support for SOC 2, HIPAA, and GDPR. Generate PDF compliance reports, export GDPR data packages, and demonstrate governance posture to auditors.',
  },
  {
    icon: <FileText className="h-6 w-6" aria-hidden="true" />,
    title: 'Audit & Reporting',
    description:
      'Every AI interaction is logged with cryptographic hash chains for tamper-evident audit. Generate PDF reports, query audit trails, and export compliance evidence in seconds.',
  },
  {
    icon: <DollarSign className="h-6 w-6" aria-hidden="true" />,
    title: 'Cost Control',
    description:
      'Set per-team and per-model budgets with real-time spend tracking. Get alerts before overruns and automatically throttle usage when limits are reached.',
  },
];

export function FeaturesGrid() {
  const [headingRef, headingInView] = useInView();

  return (
    <section
      id="features"
      className="relative py-24 sm:py-32 bg-gray-950"
      aria-labelledby="features-heading"
    >
      {/* Subtle gradient divider */}
      <div
        className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={headingRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headingInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="features-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            Everything you need to <span className="gradient-text">govern AI</span>
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Nine integrated capabilities that give you complete control over how AI is used across
            your organization — without slowing down your teams.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => (
            <FeatureCard key={feature.title} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }: { feature: Feature; index: number }) {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl border border-gray-800 bg-gray-900/50 p-8 hover:border-indigo-500/50 hover:bg-gray-900/80 transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="mb-4 inline-flex items-center justify-center rounded-lg bg-indigo-500/10 p-3 text-indigo-400 group-hover:bg-indigo-500/20 transition-colors">
        {feature.icon}
      </div>
      <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
      <p className="mt-2 text-sm text-gray-400 leading-relaxed">{feature.description}</p>
    </div>
  );
}
